"use client"

import React, { useMemo } from 'react'
import { VirtualizedSelect } from "@/components/ui/virtualized-select"
import { useInternalProfiles } from "@/hooks/use-internal-profiles"
import { useClientProfiles } from "@/hooks/use-client-profiles"
import type { WorkerProfileSummary } from "@/types/api/engagement"
import { withRefFallback } from "@/lib/utils/reference-fallback"

// Profile item structure
interface ProfileItem {
  DisplayValue: string
  Id: number
}

/**
 * Builds the option list for a profile picker, injecting a synthetic option for the currently
 * selected profile when it isn't in the (row-level-security gated) list the picker hook returned —
 * so a restricted user still sees the assigned profile read-only. When the server map resolves a
 * name we render the picker's "Name - Id" shape; when it can't (e.g. an unresolvable profile, faulty
 * MostRecent data), we degrade to the same "Profile ID {id}" label the History view uses rather than
 * suppressing the option, so an assigned-but-unresolvable profile still renders as set instead of
 * looking blank. The synthetic option is only added for a set value missing from the list — an unset
 * field (no value) still shows the placeholder. Shares the generic withRefFallback.
 */
function withProfileFallback(
  profiles: ProfileItem[],
  value: number | undefined,
  profileSummaries?: Record<number, WorkerProfileSummary>
): ProfileItem[] {
  return withRefFallback(profiles, value, p => p.Id, (v) => {
    const fullName = profileSummaries?.[v]?.fullName
    return { Id: v, DisplayValue: fullName ? `${fullName} - ${v}` : `Profile ID ${v}` }
  })
}

// ProfileSelect - Adds profiles/loading, converts number<->string, inherits validation props
interface ProfileSelectOwnProps {
  value?: number
  onChange: (value: number) => void
  profiles: ProfileItem[]
  loading: boolean
  loadingText?: string
  isReadonly?: boolean
}

type ProfileSelectProps = 
  Omit<React.ComponentPropsWithoutRef<typeof VirtualizedSelect>, 
    'options' | 'value' | 'onValueChange' | 'getItemLabel' | 'getItemValue' | 'emptyMessage'>
  & ProfileSelectOwnProps

const ProfileSelect = React.forwardRef<HTMLButtonElement, ProfileSelectProps>(({
  value,
  onChange,
  profiles,
  loading,
  loadingText = "Loading...",
  isReadonly = false,
  placeholder = "Select profile",
  searchPlaceholder = "Search...",
  disabled,
  ...rest
}, ref) => {
  return (
    <VirtualizedSelect
      ref={ref}
      {...rest}
      options={profiles}
      value={value ? String(value) : ""}
      onValueChange={(stringValue) => onChange(parseInt(stringValue) || 0)}
      getItemLabel={(profile) => profile.DisplayValue}
      getItemValue={(profile) => profile.Id.toString()}
      placeholder={placeholder}
      searchPlaceholder={searchPlaceholder}
      emptyMessage={loading ? loadingText : undefined}
      disabled={isReadonly || disabled}
    />
  )
})

// Shared base for profile selects with dependencies
type ProfileSelectWithDependencyProps = 
  Omit<ProfileSelectProps, 'profiles' | 'loading' | 'loadingText' | 'disabled' | 'value' | 'onChange'>

// Internal Profiles - fetches internal data
interface InternalProfileSelectProps extends ProfileSelectWithDependencyProps {
  value?: number
  onChange: (value: number) => void
  selectedInternalCompany: number
  /** Server-resolved fallback names so an already-assigned profile still renders for a restricted caller. */
  profileSummaries?: Record<number, WorkerProfileSummary>
}

export const InternalProfileSelect = React.forwardRef<HTMLButtonElement, InternalProfileSelectProps>(({
  value,
  onChange,
  selectedInternalCompany,
  profileSummaries,
  placeholder = "Select profile",
  isReadonly = false,
  ...rest
}, ref) => {
  const {
    internalProfiles,
    loading
  } = useInternalProfiles({ organizationId: selectedInternalCompany || 0 })

  const hasDependency = selectedInternalCompany > 0
  const effectivePlaceholder = hasDependency
    ? placeholder
    : "Select internal company first"

  const profiles = useMemo(
    () => withProfileFallback(internalProfiles, value, profileSummaries),
    [internalProfiles, value, profileSummaries]
  )

  return (
    <ProfileSelect
      ref={ref}
      {...rest}
      value={value}
      onChange={onChange}
      profiles={profiles}
      loading={loading}
      placeholder={effectivePlaceholder}
      searchPlaceholder="Search profiles..."
      loadingText="Loading profiles..."
      isReadonly={isReadonly}
      disabled={!hasDependency}
    />
  )
})

// Client Profiles - fetches client data
interface ClientProfileSelectProps extends ProfileSelectWithDependencyProps {
  value?: number
  onChange: (value: number) => void
  selectedClient: number
  /** Server-resolved fallback names so an already-assigned profile still renders for a restricted caller. */
  profileSummaries?: Record<number, WorkerProfileSummary>
}

export const ClientProfileSelect = React.forwardRef<HTMLButtonElement, ClientProfileSelectProps>(({
  value,
  onChange,
  selectedClient,
  profileSummaries,
  placeholder = "Select contact",
  isReadonly = false,
  ...rest
}, ref) => {
  const {
    clientProfiles,
    loading
  } = useClientProfiles({ organizationId: selectedClient })

  const hasDependency = selectedClient > 0
  const effectivePlaceholder = hasDependency
    ? placeholder
    : "Select client first"

  const profiles = useMemo(
    () => withProfileFallback(clientProfiles, value, profileSummaries),
    [clientProfiles, value, profileSummaries]
  )

  return (
    <ProfileSelect
      ref={ref}
      {...rest}
      value={value}
      onChange={onChange}
      profiles={profiles}
      loading={loading}
      placeholder={effectivePlaceholder}
      searchPlaceholder="Search contacts..."
      loadingText="Loading contacts..."
      isReadonly={isReadonly}
      disabled={!hasDependency}
    />
  )
})